import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

import { languageToPreload } from './languageChain';
import {
  APP_LANGUAGES,
  LOCALE_FOLDER,
  fromBackendLanguage,
  migrateStoredLanguage,
  type AppLanguage,
} from './languageCodes';

/**
 * The languages the app ships, in the order they should be offered.
 *
 * Exported so nothing has to restate it: a language added here reaches the
 * config below and every picker that reads from it, instead of being added in
 * one place and forgotten in the other.
 */
export const SUPPORTED_LANGUAGES = APP_LANGUAGES;

export type SupportedLanguage = AppLanguage;

/**
 * AL-1778: the four languages used to be imported at the top of this file, so
 * they landed in the entry chunk — 71% of `index-*.js`, around 263 kB gzip of
 * translations that every visitor downloaded to read one of them. And because
 * the entry chunk changes on every deploy, they came down again on nearly every
 * revisit.
 *
 * A dynamic import inside the loader is what makes Vite emit one chunk per
 * language, cached on its own and fetched only when a language is actually
 * used. The `@vite-ignore`-free template literal is deliberate: Vite needs to
 * see the shape of the path to know which files to split out.
 */
const backend = resourcesToBackend((language: string, _namespace: string) => {
  // AL-1843: the language is BCP 47 (`pt-BR`), the folder keeps its old name (`br`).
  const folder = LOCALE_FOLDER[language as AppLanguage] ?? language;
  return import(`../locales/${folder}/translation.json`);
});

// Before init: a `br` cached by an older build is not a supported code any more.
migrateStoredLanguage(typeof window === 'undefined' ? undefined : window.localStorage);

const DEFAULT_FALLBACK: AppLanguage =
  fromBackendLanguage(import.meta.env.VITE_FALLBACK_LOCALE) ?? 'en';

i18n
  .use(backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // No forced `lng`: let LanguageDetector resolve (localStorage → querystring →
    // navigator). Forcing `lng` here disabled detection entirely, so the saved
    // user preference was ignored on every page load.
    fallbackLng: {
      'pt-BR': ['pt-PT', 'en'], // PT-BR falls back to PT-PT before English
      default: [DEFAULT_FALLBACK],
    },
    // BCP 47 codes (AL-1843), so Intl.PluralRules applies Portuguese rules and
    // not Breton ones. A bare browser `pt` (or `pt-AO`) resolves to the first
    // supported `pt-*`, i.e. pt-PT. The backend's codes are mapped in
    // languageCodes.ts, never passed here.
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // Only the exact code: with region codes i18next would otherwise also try
    // to load the bare `pt`, which has no bundle.
    load: 'currentOnly',
    debug: import.meta.env.DEV && import.meta.env.VITE_ENABLE_CONSOLE_LOGS === 'true',

    // Use single default namespace for simplicity
    ns: ['translation'],
    defaultNS: 'translation',
    fallbackNS: 'translation',
    returnObjects: true,

    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['localStorage', 'querystring', 'navigator'],
      caches: ['localStorage'],
    },
    // Add a missing key handler for a better development experience.
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (lng, _ns, key) => {
      if (import.meta.env.DEV) {
        console.warn(`[i18n] Missing key "${key}" for language "${lng}"`);
      }
    },
  });

/**
 * Resolves once the active language is in memory (AL-1778).
 *
 * `main.tsx` awaits this before the first render. Without it the app paints
 * with the translations still in flight, which is a screen of raw keys that
 * then flips to text: worse than the kilobytes this ticket removes.
 *
 * Measured, not assumed: an `en` visitor fetches one chunk, `es` two and `br`
 * three. i18next resolves the fallback chain when it initialises, so a language
 * that falls back pulls its chain with it — `load: 'currentOnly'` does not
 * change that, it only governs regional variants. Dropping `fallbackLng` from
 * the init would buy those kilobytes by giving up the br → pt → en resolution,
 * which is a real property of the product and worth more than the bytes.
 */
export const i18nReady: Promise<unknown> = i18n.loadLanguages(
  languageToPreload(i18n.language, SUPPORTED_LANGUAGES)
);

export default i18n;
